import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  Box, Typography, Card, CardContent, TextField, InputAdornment, Chip, Button,
  CircularProgress, IconButton, Collapse, Tooltip, Stack, Grid, Paper, Divider,
  Dialog, DialogTitle, DialogContent, DialogActions, Alert,
} from '@mui/material';
import {
  Search as SearchIcon, ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon, Domain as EnterpriseIcon,
  AccountTree as OrgIcon, Map as RegionIcon, Business as OfficeIcon,
  UnfoldMore as ExpandAllIcon, UnfoldLess as CollapseAllIcon,
  OpenInNew as OpenIcon, Refresh as RefreshIcon, DeleteOutline as DeleteIcon,
  LoginOutlined as EnterIcon,
} from '@mui/icons-material';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

/**
 * Every customer, at every level, whether or not it holds a single clinic.
 *
 * The Clinics list cannot answer this — it is a list of clinics, so an enterprise
 * onboarded this morning, or an organisation whose admin has not opened their first
 * site yet, does not appear on it at all. A customer that had just been created looked
 * like it had failed to be created. So this is the tree instead, and an EMPTY node is a
 * first-class row here rather than an omission.
 *
 * One request. The balances arrive with the structure (the server batches them), so
 * nothing here fetches per row.
 */

// How each level is drawn. Colour carries the depth as much as the indent does, because
// a deep tree read by indent alone is hard to scan.
const LEVELS = {
  enterprise: { icon: <EnterpriseIcon fontSize="small" />, label: 'Enterprise', color: '#7c3aed' },
  organization: { icon: <OrgIcon fontSize="small" />, label: 'Organisation', color: '#2563eb' },
  region: { icon: <RegionIcon fontSize="small" />, label: 'Region', color: '#0891b2' },
  clinic: { icon: <OfficeIcon fontSize="small" />, label: 'Office', color: '#059669' },
};

// What a node is short of, in the words of its own level. An office that has never been
// granted anything is not "empty" — it is ready to work — so only the grouping levels
// get this treatment.
const EMPTY_HINT = {
  enterprise: 'No organisations yet',
  organization: 'No sites yet',
  region: 'No offices yet',
};

/** Sessions, phrased for whichever kind of node is holding them. */
function creditLabel(node) {
  const c = node.credits || {};
  // A grouping level with no account row of its own holds no pool — and calling that
  // "Unlimited" was actively misleading, because what it can actually hand down depends
  // on whether anything above it is metered (see ai_credits.metered_ancestor). "No
  // pool" is true either way. An OFFICE with no account really is uncapped, so it keeps
  // the old wording.
  if (!c.has_account && node.scope !== 'clinic') {
    return { text: 'No pool', tone: 'default' };
  }
  if (c.unlimited) return { text: 'Unlimited', tone: 'success' };
  if (node.scope === 'clinic') {
    const left = c.remaining ?? 0;
    return {
      text: `${left} of ${c.granted ?? 0} left`,
      tone: left <= 0 ? 'error' : left < 10 ? 'warning' : 'default',
    };
  }
  // A pool hands sessions down and spends none itself, so what matters is what is left
  // to give — not a "remaining" that would read like an allowance.
  const available = c.available ?? 0;
  return {
    text: `${available} to give of ${c.granted ?? 0}`,
    tone: available <= 0 ? 'warning' : 'default',
  };
}

function countSummary(node) {
  const bits = [];
  if (node.scope === 'enterprise') {
    bits.push(`${node.organization_count} org${node.organization_count === 1 ? '' : 's'}`);
    bits.push(`${node.office_count} office${node.office_count === 1 ? '' : 's'}`);
  }
  if (node.scope === 'organization') {
    if (node.region_count) bits.push(`${node.region_count} region${node.region_count === 1 ? '' : 's'}`);
    bits.push(`${node.office_count} site${node.office_count === 1 ? '' : 's'}`);
  }
  if (node.scope === 'region') {
    bits.push(`${node.office_count} office${node.office_count === 1 ? '' : 's'}`);
  }
  if (node.admin_count) bits.push(`${node.admin_count} admin${node.admin_count === 1 ? '' : 's'}`);
  return bits.join(' · ');
}

// Which endpoint owns each level's delete. Offices are not closed from here — the
// Clinics screen already owns that flow, including its own confirmation.
const DELETE_PATH = {
  enterprise: 'enterprises',
  organization: 'organizations',
  region: 'regions',
};

function Row({ node, depth, expanded, onToggle, onOpenOffice, onClose, onEnter, entering }) {
  const level = node.is_region_practice
    ? { ...LEVELS.clinic, label: 'Region practice' }
    : (LEVELS[node.scope] || LEVELS.clinic);
  const kids = node.children || [];
  const isOpen = expanded.has(node.id);
  const credit = creditLabel(node);
  const counts = countSummary(node);

  return (
    <>
      <Box
        sx={{
          display: 'flex', alignItems: 'center', gap: 1,
          pl: 1 + depth * 3, pr: 1.5, py: 1,
          borderLeft: `3px solid ${level.color}`,
          bgcolor: depth === 0 ? 'action.hover' : 'transparent',
          opacity: node.is_active === false ? 0.55 : 1,
          '&:hover': { bgcolor: 'action.selected' },
          borderBottom: '1px solid', borderBottomColor: 'divider',
        }}
      >
        <IconButton
          size="small"
          onClick={() => onToggle(node.id)}
          disabled={!kids.length}
          // Kept in the layout even with nothing to expand, so names stay aligned
          // down a column instead of stepping in and out by a chevron's width.
          sx={{ visibility: kids.length ? 'visible' : 'hidden' }}
          aria-label={isOpen ? `Collapse ${node.name}` : `Expand ${node.name}`}
        >
          {isOpen ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
        </IconButton>

        <Box sx={{ color: level.color, display: 'flex' }}>{level.icon}</Box>

        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
            <Typography variant="body2" fontWeight={depth === 0 ? 700 : 600} noWrap>
              {node.name}
            </Typography>
            <Chip label={level.label} size="small" variant="outlined"
              sx={{ height: 20, fontSize: 11, borderColor: level.color, color: level.color }} />
            {node.is_active === false && (
              <Chip label="Switched off" size="small" color="default" sx={{ height: 20, fontSize: 11 }} />
            )}
            {node.is_empty && EMPTY_HINT[node.scope] && (
              <Tooltip title="Created, but nothing in it yet — its administrator adds the first one.">
                <Chip label={EMPTY_HINT[node.scope]} size="small" color="warning"
                  variant="outlined" sx={{ height: 20, fontSize: 11 }} />
              </Tooltip>
            )}
          </Stack>
          {counts && (
            <Typography variant="caption" color="text.secondary">{counts}</Typography>
          )}
        </Box>

        <Chip label={credit.text} size="small"
          color={credit.tone === 'default' ? undefined : credit.tone}
          variant={credit.tone === 'default' ? 'outlined' : 'filled'}
          sx={{ height: 22, fontSize: 11 }} />

        {node.scope === 'clinic' ? (
          <Tooltip title="Open this clinic">
            <IconButton size="small" onClick={() => onOpenOffice(node.id)} aria-label={`Open ${node.name}`}>
              <OpenIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : (
          <>
            {/* Work inside this level as its administrator — the same audited entry
                the clinic list offers, one level up. Refused for a switched-off node,
                so it is not offered either. */}
            <Tooltip title={node.is_active === false
              ? 'Switch it back on to enter'
              : `Enter ${node.name} as its administrator`}>
              <span>
                <IconButton size="small" color="primary" disabled={node.is_active === false || entering}
                  onClick={() => onEnter(node)} aria-label={`Enter ${node.name}`}>
                  {entering ? <CircularProgress size={16} /> : <EnterIcon fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={node.is_empty ? `Delete ${node.name}` : `Close ${node.name}`}>
              <IconButton size="small" color="error" onClick={() => onClose(node)}
                aria-label={`${node.is_empty ? 'Delete' : 'Close'} ${node.name}`}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        )}
      </Box>

      {!!kids.length && (
        <Collapse in={isOpen} timeout="auto" unmountOnExit>
          {kids.map((child) => (
            <Row key={child.id} node={child} depth={depth + 1} expanded={expanded}
              onToggle={onToggle} onOpenOffice={onOpenOffice} onClose={onClose}
              onEnter={onEnter} entering={entering === child.id} />
          ))}
        </Collapse>
      )}
    </>
  );
}

/** Every id in the tree, so "expand all" doesn't have to walk it twice. */
function allIds(nodes, acc = []) {
  nodes.forEach((n) => {
    acc.push(n.id);
    if (n.children?.length) allIds(n.children, acc);
  });
  return acc;
}

/**
 * Keep a node when it matches, or when anything beneath it does — a search for an
 * office has to leave its enterprise and organisation standing or the result has
 * nowhere to sit.
 */
function filterTree(nodes, q) {
  if (!q) return nodes;
  const out = [];
  nodes.forEach((n) => {
    const kids = filterTree(n.children || [], q);
    if (n.name.toLowerCase().includes(q) || kids.length) {
      out.push({ ...n, children: kids });
    }
  });
  return out;
}

export default function Customers() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(new Set());
  // Close flow: { node, contents } once the reach has been fetched. `typed` is the
  // name the operator has to retype before a cascade is allowed to run.
  const [closing, setClosing] = useState(null);
  const [typed, setTyped] = useState('');
  const [closeBusy, setCloseBusy] = useState(false);
  const [entering, setEntering] = useState(null);     // node id while the handoff runs

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/v1/superadmin/customers`);
      setData(res.data);
      // Open the top level on arrival. Everything collapsed hides the tree's whole
      // point; everything open buries it on a large platform.
      setExpanded(new Set((res.data.customers || []).map((n) => n.id)));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load customers');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = useCallback((id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const openClose = useCallback(async (node) => {
    setTyped('');
    // Ask the server what the cascade would reach before showing any warning. An
    // operator about to close an enterprise needs the office and patient counts in
    // front of them, not in the response afterwards.
    setClosing({ node, contents: null });
    try {
      const { data } = await axios.get(
        `${API_URL}/api/v1/superadmin/${DELETE_PATH[node.scope]}/${node.id}/contents`);
      setClosing({ node, contents: data });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not check what that would affect');
      setClosing(null);
    }
  }, []);

  const confirmClose = useCallback(async () => {
    if (!closing || closeBusy) return;
    const { node } = closing;
    setCloseBusy(true);
    try {
      const { data } = await axios.delete(
        `${API_URL}/api/v1/superadmin/${DELETE_PATH[node.scope]}/${node.id}`,
        { params: { force: !node.is_empty } });
      toast.success(data.message || 'Done');
      setClosing(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'That could not be completed');
    } finally {
      setCloseBusy(false);
    }
  }, [closing, closeBusy, load]);

  const enterNode = useCallback(async (node) => {
    setEntering(node.id);
    try {
      // Returns a one-time ~60s handoff URL into the clinic portal; the real session
      // token is minted there on redemption and never travels in this URL.
      const { data } = await axios.post(
        `${API_URL}/api/v1/superadmin/${DELETE_PATH[node.scope]}/${node.id}/enter`);
      window.location.assign(data.handoff_url);
    } catch (err) {
      toast.error(err.response?.data?.detail || `Could not enter ${node.name}`);
      setEntering(null);
    }
  }, []);

  const q = search.trim().toLowerCase();
  const shown = useMemo(() => filterTree(data?.customers || [], q), [data, q]);

  // A search only helps if what it found is visible, so matches open on their own.
  useEffect(() => {
    if (q) setExpanded(new Set(allIds(shown)));
  }, [q, shown]);

  const totals = data?.totals;

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between"
        sx={{ mb: 1, flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>Customers</Typography>
          <Typography variant="body2" color="text.secondary">
            Every account by level — enterprises, organisations, regions and offices.
            Accounts with nothing in them yet are listed too.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button size="small" startIcon={<ExpandAllIcon />}
            onClick={() => setExpanded(new Set(allIds(shown)))}>Expand all</Button>
          <Button size="small" startIcon={<CollapseAllIcon />}
            onClick={() => setExpanded(new Set())}>Collapse all</Button>
          <Button size="small" startIcon={<RefreshIcon />} onClick={load}>Refresh</Button>
          <Button size="small" variant="contained" onClick={() => navigate('/clinics')}>
            New customer
          </Button>
        </Stack>
      </Stack>

      {totals && (
        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          {[
            { k: 'enterprises', label: 'Enterprises', scope: 'enterprise' },
            { k: 'organizations', label: 'Organisations', scope: 'organization' },
            { k: 'regions', label: 'Regions', scope: 'region' },
            { k: 'offices', label: 'Offices', scope: 'clinic' },
          ].map(({ k, label, scope }) => (
            <Grid item xs={6} sm={3} key={k}>
              <Paper variant="outlined" sx={{ p: 1.5, borderTop: `3px solid ${LEVELS[scope].color}` }}>
                <Typography variant="h5" fontWeight={700}>{totals[k]}</Typography>
                <Typography variant="caption" color="text.secondary">{label}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      {!!(totals?.empty_enterprises || totals?.empty_organizations) && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          {totals.empty_enterprises} enterprise(s) and {totals.empty_organizations}{' '}
          organisation(s) have nothing in them yet — their own administrators add the first one.
        </Typography>
      )}

      <Card>
        <CardContent sx={{ pb: 1 }}>
          <TextField
            fullWidth size="small" placeholder="Search any level by name…"
            value={search} onChange={(e) => setSearch(e.target.value)}
            InputProps={{ startAdornment: (
              <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
            ) }}
          />
        </CardContent>
        <Divider />
        {loading ? (
          <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress /></Box>
        ) : !shown.length ? (
          <Box sx={{ p: 6, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {q ? 'Nothing matches that search.' : 'No customers yet.'}
            </Typography>
          </Box>
        ) : (
          <Box>
            {shown.map((node) => (
              <Row key={node.id} node={node} depth={0} expanded={expanded}
                onToggle={toggle} onOpenOffice={(id) => navigate(`/clinics/${id}`)}
                onClose={openClose} onEnter={enterNode}
                entering={entering === node.id} />
            ))}
          </Box>
        )}
      </Card>

      <Dialog open={Boolean(closing)} onClose={() => !closeBusy && setClosing(null)}
        maxWidth="sm" fullWidth>
        <DialogTitle>
          {closing?.node.is_empty
            ? `Delete ${closing?.node.name}?`
            : `Close ${closing?.node.name}?`}
        </DialogTitle>
        <DialogContent>
          {!closing?.contents ? (
            <Box sx={{ py: 3, textAlign: 'center' }}><CircularProgress size={24} /></Box>
          ) : closing.node.is_empty ? (
            <Typography variant="body2">
              <strong>{closing.node.name}</strong> is empty, so it is deleted outright.
              There is nothing beneath it and nothing to keep.
            </Typography>
          ) : (
            <Box>
              <Alert severity="error" sx={{ mb: 2 }}>
                This closes <strong>{closing.node.name}</strong> and everything beneath it:
                {closing.contents.organizations ? ` ${closing.contents.organizations} organisation(s),` : ''}
                {closing.contents.regions ? ` ${closing.contents.regions} region(s),` : ''}
                {` ${closing.contents.offices} office(s)`}. Nobody who worked there will be
                able to sign in again.
              </Alert>
              <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                No patient data is deleted. All {closing.contents.patients} patient record(s),
                with their visits, notes and recordings, are kept.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Protocols, AI session pools, queues and settings are deleted. Staff accounts
                are deactivated rather than removed, so every record still names who wrote it.
              </Typography>
              {!!closing.contents.office_names?.length && (
                <Typography variant="caption" color="text.secondary"
                  sx={{ display: 'block', mb: 2 }}>
                  Offices: {closing.contents.office_names.join(', ')}
                  {closing.contents.offices > closing.contents.office_names.length ? ' …' : ''}
                </Typography>
              )}
              {/* Typed confirmation, because one click here can close dozens of sites. */}
              <TextField fullWidth size="small" value={typed}
                onChange={(e) => setTyped(e.target.value)}
                label={`Type "${closing.node.name}" to confirm`}
                autoComplete="off" />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setClosing(null)} disabled={closeBusy}>Cancel</Button>
          <Button color="error" variant="contained" onClick={confirmClose}
            disabled={
              closeBusy || !closing?.contents
              || (!closing?.node.is_empty && typed.trim() !== closing?.node.name)
            }
            startIcon={closeBusy ? <CircularProgress size={16} color="inherit" /> : null}>
            {closing?.node.is_empty ? 'Delete' : 'Close the account'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
